<template>
  <b-container class="chat-box">
    <b-row>
      <b-col cols="12" lg="10" class="mx-auto">
        <!-- Interaction component first, chatbot below it. -->
        <MaterialsBankDemo
          :style="{
            '--main-color': mainColor,
            '--secondary-color': secondaryColor,
          }"
          :context="context"
          class="mb-4"
          @go-to-step="handleGoToStep"
        />
        <div
          v-for="(msg, msgidx) in messages"
          :key="msgidx"
          class="chat-record mt-2"
        >
          <ChatMessageComponent
            :style="{
              '--main-color': mainColor,
              '--secondary-color': secondaryColor,
            }"
            :initMsg="msg"
            :key="msgidx"
            :msgidx="msgidx"
            :ref="`msgRef-${msg.name}`"
            :context="context"
            :condition="condition"
            @select-card="handleCardClick"
            @continue="continueChat"
            @go-to-step="handleGoToStep"
            @scroll-to-bottom="scrollToBottom"
            @scroll-to-msg="scrollToMsg"
          />
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>
<script>
// Logged-out demo for the Pruebas Informales product. Reached when a visitor
// clicks the materials teaser (see Default.changeView). It reuses the exact
// same machinery as the logged-in chatbot (ChatMessageComponent + a
// `questions` graph) so it looks and feels identical.
//
// La vitrina (MaterialsBankDemo) va arriba del chat, siempre. El chat abre con
// lo mismo que cuenta /pruebas-de-especialidad/ —el cuerpo de esa
// página, con sus viñetas animadas (PruebasSecciones)— y después el menú:
// descargar de la vitrina, preguntas frecuentes, precios o iniciar sesión.
// Las preguntas y los planes bajan del endpoint de la página, igual que en
// Eureka y Planificaciones.
import ChatMessageComponent from "./ChatMessageComponent.vue";
import { fetchRewards, applyBonus, loginLabel } from "./signupRewards";
import MaterialsBankDemo from "./MaterialsBankDemo.vue";
import PruebasSecciones from "./PruebasSecciones.vue";
import informationService from "@/services/informationService";
import { sectionStep, sectionComponent } from "./serviceSections";

// El rojo de Pruebas de especialidad (`accent_key='redpie'` en pages.py) y su
// versión oscura para texto.
const ACENTO = "#e74a57";
const ACENTO_INK = "#b4303c";

export default {
  name: "MaterialsDemo",
  components: { ChatMessageComponent, MaterialsBankDemo },
  // Absorb the props the layout's router-view binds so they don't leak onto the
  // root element; none of them are needed by the demo.
  props: {
    bannerLoaded: { type: Boolean, required: false, default: false },
    routeName: { type: String, required: false, default: "" },
    tags: { type: Array, required: false, default: () => [] },
  },
  data() {
    return {
      mainColor: "#e84855",
      secondaryColor: "#e48692",
      currentQuestionIndex: "",
      condition: "",
      // The interaction component sits above this chat; don't autoscroll to the
      // chat (stealing focus) until the visitor actually engages with it.
      hasInteracted: false,
      context: {},
      messages: [],
      questions: {
        // La llegada: el saludo y el cuerpo de la página de producto. El menú
        // aparece solo debajo, y como al llegar queda `details` en `context`,
        // el menú deja de ofrecerlo.
        details: sectionStep("details", "👋 ¡Hola!", {
          component: PruebasSecciones,
          dontShowNextButton: true,
          // El botón de Google va bajo el saludo, para entrar sin bajar hasta
          // el menú.
          loginButton: true,
        }),
        mainMessage: {
          name: "mainMessage",
          isMain: true,
          text: "<strong>¿Qué te gustaría hacer?</strong>",
          type: "options",
          answersLoaded: false,
          single_choice: true,
          answers: [],
          process_dic: [
            {
              name: "Iniciar sesión para probar la versión real",
              cardClass: "highlighted-option pulse-option",
              option: "login",
            },
            {
              name: "Descargar pruebas de la vitrina",
              nextQuestionIndex: "bank",
              // La vitrina es una herramienta, no una lectura: se puede volver
              // a ella cuantas veces haga falta.
              always: true,
            },
            {
              name: "Conocer más sobre el servicio",
              nextQuestionIndex: "details",
            },
            {
              name: "Ver las preguntas frecuentes",
              nextQuestionIndex: "faq",
            },
            {
              name: "Ver los precios y planes",
              nextQuestionIndex: "prices",
            },
          ],
          // Rebuilt on every visit: a step already taken has its name as a key
          // in `context`, so its button is dropped instead of coming back —
          // and the fresh objects keep a past `isSelected` from sticking.
          getAnswers(context) {
            this.answers = this.process_dic
              .filter(
                (ans) =>
                  ans.always ||
                  !ans.nextQuestionIndex ||
                  !(ans.nextQuestionIndex in context)
              )
              .map((ans) => ({
                ...ans,
                isSelected: false,
                class: "col-md-7 col-12 my-2",
                bootstrap_classes: "text-left px-2 font-weight-bold",
              }));
            return Promise.resolve(this.answers);
          },
          routine() {},
        },
        bank: {
          name: "bank",
          text: "¡Genial! Estos son los materiales de nuestra vitrina, listos para descargar. Para <strong>aplicarlos y corregirlos</strong> con tus estudiantes necesitas iniciar sesión.",
          type: "custom-component",
          component: MaterialsBankDemo,
          single_choice: false,
          answersLoaded: true,
          answers: [],
          dontShowNextButton: true,
          nextQuestionIndex: "mainMessage",
          routine() {},
        },
        faq: sectionStep(
          "faq",
          "Estas son las preguntas que más nos hacen sobre las pruebas:",
          { dontShowNextButton: true }
        ),
        prices: sectionStep(
          "prices",
          "Las pruebas son gratis. Esto es lo que cuesta corregirlas en línea:",
          { dontShowNextButton: true }
        ),
      },
    };
  },
  created() {
    this.$store.commit("chatbot/clear");
    // Los usos gratis salen de la config y las preguntas y los planes, de la
    // página de producto. Si alguna falla, esa mitad simplemente no se cuenta.
    Promise.all([
      fetchRewards("Pruebas Informales"),
      informationService
        .getServiceContent("pruebas-de-especialidad")
        .catch(() => null),
    ]).then(([rewards, content]) => {
      this.applyContent(content);
      applyBonus(this.questions, rewards, ["mainMessage"]);
      this.questions.details.loginLabel = loginLabel(rewards, "Iniciar sesión para probar la versión real");
      this.currentQuestionIndex = "details";
      const question = this.questions[this.currentQuestionIndex];
      this.context[question.name] = {};
      this.messages.push(question);
    });
  },
  computed: {
    token() {
      return this.$store.state.login.token;
    },
    currentQuestion() {
      return this.questions[this.currentQuestionIndex];
    },
  },
  watch: {
    // Once the visitor logs in (from a CTA), send them to the real product.
    token(val) {
      if (val) this.goToRealProduct();
    },
  },
  mounted() {
    // A logged-in user has no business on the demo (e.g. landed here after a
    // login that reloaded the page) — forward them to the real chatbot.
    if (this.token) this.goToRealProduct();
  },
  methods: {
    // Reparte el contenido de la página de producto. Sin contenido —el
    // endpoint no respondió— el FAQ y los precios no tienen de dónde salir y
    // se retiran del menú: mejor no ofrecerlos que abrir una sección vacía.
    applyContent(content) {
      if (content) {
        if (content.hero_lede) {
          this.questions.details.text += ` ${content.hero_lede}`;
        }
        this.questions.faq.component = sectionComponent(
          content,
          "faq",
          ACENTO,
          ACENTO_INK
        );
        this.questions.prices.component = sectionComponent(
          content,
          "planes",
          ACENTO,
          ACENTO_INK
        );
        return;
      }
      const menu = this.questions.mainMessage;
      menu.process_dic = menu.process_dic.filter(
        (ans) => !["faq", "prices"].includes(ans.nextQuestionIndex)
      );
    },
    handleCardClick(answer, msgidx, ansidx, max) {
      this.hasInteracted = true;
      if (this.messages[msgidx].name === this.currentQuestion.name) {
        this.selectCard(msgidx, ansidx, max);
        this.selectAnswer();
      }
    },
    selectCard(msgidx, ansidx) {
      if (this.currentQuestion.single_choice) {
        this.messages[msgidx].answers.forEach((ans) => {
          ans.isSelected = false;
        });
      }
      this.messages[msgidx].answers[ansidx].isSelected =
        !this.messages[msgidx].answers[ansidx].isSelected;
    },
    selectAnswer() {
      if (this.currentQuestion.single_choice) {
        this.continueChat();
      }
    },
    async continueChat() {
      // Resolve the selected answer (for single-choice option steps) so we can
      // both route on it and intercept the "login" action.
      if (
        this.currentQuestion.type === "options" &&
        this.currentQuestion.single_choice
      ) {
        const selected = this.currentQuestion.answers.find(
          (ans) => ans.isSelected
        );
        this.context[this.currentQuestion.name] = selected;
        if (selected && selected.option === "login") {
          this.login();
          return;
        }
      }

      this.messages.slice(-1)[0].answered = true;
      if (this.currentQuestion.isFinal) return;

      await this.currentQuestion.routine();
      this.currentQuestionIndex =
        this.context[this.currentQuestion.name] &&
        this.context[this.currentQuestion.name].nextQuestionIndex
          ? this.context[this.currentQuestion.name].nextQuestionIndex
          : this.currentQuestion.nextQuestionIndex;
      this.loadQuestion(this.currentQuestionIndex);
    },
    loadQuestion(questionIndex) {
      try {
        const question = this.questions[questionIndex];
        this.context[question.name] = {};
        this.messages.push(question);
      } catch (error) {
        console.log(error);
      }
    },
    scrollToBottom() {
      if (!this.hasInteracted) return;
      const container = document.querySelector(".general-container");
      if (container) window.scrollTo(0, container.scrollHeight);
    },
    scrollToMsg(name) {
      if (!this.hasInteracted) return;
      const msgRef = this.$refs[`msgRef-${name}`];
      if (!msgRef || !msgRef.length) return;
      // Una sección sin botón (la vitrina, el FAQ, los precios) añade el menú
      // sola: hacerle caso al scroll arrastraría al visitante más allá de lo
      // que acaba de abrir.
      const idx = this.messages.map((m) => m.name).lastIndexOf(name);
      const previo = idx > 0 ? this.messages[idx - 1] : null;
      if (previo && previo.dontShowNextButton) return;
      const parentMsg = msgRef.at(-1).$el.parentNode;
      parentMsg.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
    },
    // The materials-bank component and the plan buttons ask to log in via a
    // `go-to-step` event; route "login" to the login modal, anything else to
    // the matching chat step.
    handleGoToStep(step) {
      this.hasInteracted = true;
      if (step === "login") {
        this.login();
        return;
      }
      if (this.questions[step]) {
        this.currentQuestionIndex = step;
        this.loadQuestion(step);
      }
    },
    login() {
      this.$emit("open-login");
    },
    goToRealProduct() {
      if (this.$router.currentRoute.name !== "materials") {
        this.$router.push("materials").catch(() => {});
      }
    },
  },
};
</script>
<style scoped>
.chat-box {
  margin: 10px auto;
  width: 100%;
  align-items: space-between;
  justify-content: space-between;
}
</style>
